import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../../utils/api";
import ShortsItem from "./ShortsItem";
import ShortsOverlay from "./ShortsOverlay";
import VideoComments from "../../components/comments/VideoComments";

const GUEST_USER_ID = "000000000000000000000000";
const PAGE_LIMIT = 20;

export default function Shorts() {
  const { id } = useParams();

  const [shorts, setShorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [currentUser] = useState(() => {
    const u = localStorage.getItem("user");
    return u ? JSON.parse(u) : null;
  });

  const [commentShortId, setCommentShortId] = useState(null);

  const containerRef = useRef(null);
  const fetchingRef = useRef(false);

  /* ================= RESPONSIVE DETECTOR ================= */
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getViewportHeight = () =>
    containerRef.current?.clientHeight || window.innerHeight;

  /* ================= RESET ON ROUTE CHANGE ================= */
  useEffect(() => {
    setShorts([]);
    setPage(1);
    setHasMore(true);
    setActiveIndex(0);
    setLoading(true);
  }, [id]);

  /* ================= FETCH SHORTS ================= */
  const fetchShorts = useCallback(
    async (pageNo) => {
      if (fetchingRef.current || !hasMore) return;
      fetchingRef.current = true;

      try {
        const res = await api.get("/client/getShorts", {
          params: {
            userId: currentUser?._id || GUEST_USER_ID,
            start: pageNo,
            limit: PAGE_LIMIT,
          },
        });

        const list = Array.isArray(res.data?.shorts)
          ? res.data.shorts
          : [];

        if (!list.length) {
          setHasMore(false);
          return;
        }

        setShorts((prev) => {
          const merged = [...prev, ...list];

          if (id) {
            const idx = merged.findIndex(
              (s) => s._id === id || s.uniqueVideoId === id
            );

            if (idx >= 0) {
              setTimeout(() => {
                containerRef.current?.scrollTo({
                  top: idx * getViewportHeight(),
                  behavior: "auto",
                });
                setActiveIndex(idx);
              }, 100);
            }
          }

          return merged;
        });
      } catch (err) {
        console.error("Fetch Shorts Error:", err);
        setHasMore(false);
      } finally {
        fetchingRef.current = false;
        setLoading(false);
      }
    },
    [currentUser, hasMore, id]
  );

  useEffect(() => {
    fetchShorts(1);
  }, [currentUser, id]);

  /* ================= SCROLL ================= */
  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;

    const height = getViewportHeight();
    const index = Math.round(el.scrollTop / height);

    if (index !== activeIndex) setActiveIndex(index);

    const nearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - height;

    if (nearBottom && hasMore && !fetchingRef.current) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchShorts(nextPage);
    }
  };

  const scrollToIndex = (idx) => {
    if (idx < 0 || idx >= shorts.length) return;

    const el = containerRef.current;
    if (!el) return;

    el.scrollTo({
      top: idx * getViewportHeight(),
      behavior: "smooth",
    });

    setActiveIndex(idx);
  };

  if (loading && !shorts.length) {
    return (
      <div style={{ textAlign: "center", marginTop: 40 }}>
        Loading...
      </div>
    );
  }


const activeShort = shorts[activeIndex];
  return (
    <>

<Helmet>
  <title>
    {activeShort
      ? `${activeShort.title || "Shorts"} | ShareVideo`
      : "Shorts - ShareVideo"}
  </title>

  <meta
    name="description"
    content={
      activeShort?.description
        ? activeShort.description.slice(0, 150)
        : "Watch trending short videos on ShareVideo. Discover creative and entertaining vertical shorts from talented creators."
    }
  />

  <link
    rel="canonical"
    href={
      activeShort
        ? `${window.location.origin}/shorts/${activeShort._id}`
        : `${window.location.origin}/shorts`
    }
  />
</Helmet>

      {/* ===== WRAPPER ===== */}
      <div
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: isMobile ? "center" : "center",
          alignItems: "center",
          background: isMobile ? "#000" : "#e5e7eb",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* ===== MOBILE / DESKTOP FRAME ===== */}
        <div
          style={{
            width: isMobile ? "100%" : 390,
            height: isMobile ? "100vh" : "92vh",
            background: "#000",
            borderRadius: isMobile ? 0 : 32,
            overflow: "hidden",
            boxShadow: isMobile
              ? "none"
              : "0 40px 100px rgba(0,0,0,0.35)",
            position: "relative",
          }}
        >
          <div
            ref={containerRef}
            onScroll={handleScroll}
            style={{
              height: "100%",
              overflowY: "auto",
              scrollSnapType: "y mandatory",
              background: "#000",
            }}
          >
            {shorts.map((short, index) => (
              <div
                key={short._id}
                style={{
                  height: "100%",
                  scrollSnapAlign: "start",
                }}
              >
                <ShortsItem
                  data={short}
                  isActive={activeIndex === index}
                  currentUser={currentUser}
                />
              </div>
            ))}
          </div>

          {/* ===== OVERLAY (INSIDE FOR MOBILE) ===== */}
          {isMobile && shorts[activeIndex] && (
            <ShortsOverlay
              data={shorts[activeIndex]}
              currentUser={currentUser}
              onOpenComments={(id) => setCommentShortId(id)}
              onScrollUp={() => scrollToIndex(activeIndex - 1)}
              onScrollDown={() => scrollToIndex(activeIndex + 1)}
            />
          )}
        </div>

        {/* ===== OVERLAY OUTSIDE FOR DESKTOP ===== */}
        {!isMobile && shorts[activeIndex] && (
          <div
            style={{
              position: "absolute",
              right: `calc(50% - 390px / 2 - 80px)`,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 50,
            }}
          >
            <ShortsOverlay
              data={shorts[activeIndex]}
              currentUser={currentUser}
              onOpenComments={(id) => setCommentShortId(id)}
              onScrollUp={() => scrollToIndex(activeIndex - 1)}
              onScrollDown={() => scrollToIndex(activeIndex + 1)}
            />
          </div>
        )}
      </div>

      {/* ===== COMMENTS MODAL ===== */}
      {commentShortId && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 999,
          }}
          onClick={() => setCommentShortId(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              bottom: 0,
              width: "100%",
              maxHeight: "80%",
              background: "#fff",
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              padding: 12,
              overflowY: "auto",
            }}
          >
            <VideoComments
              videoId={commentShortId}
              currentUser={currentUser}
            />
          </div>
        </div>
      )}
    </>
  );
}
